import React from "react";
import PropTypes from "prop-types";

import styles from "./SkeletonLayout.module.scss";

const DEFAULT_ITEMS = [
  {
    type: "block",
    width: "48%",
    height: 14,
  },
  {
    type: "block",
    width: "72%",
    height: 12,
  },
  {
    type: "group",
    layout: "row",
    gap: 8,
    items: [
      { type: "block", width: 84, height: 26, radius: 999 },
      { type: "block", width: 84, height: 26, radius: 999 },
      { type: "block", width: 84, height: 26, radius: 999 },
    ],
  },
  {
    type: "group",
    layout: "grid",
    columns: 3,
    gap: 12,
    className: "defaultCardsGroup",
    items: [
      { type: "block", height: 156, className: "defaultCardBlock" },
      { type: "block", height: 156, className: "defaultCardBlock" },
      { type: "block", height: 156, className: "defaultCardBlock" },
    ],
  },
];

const defaultBlock = {
  width: "100%",
  height: 12,
  radius: 8,
};

const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const toCssLength = (value) => {
  if (isFiniteNumber(value)) return `${value}px`;
  return value;
};

const getNodeClassName = (className) => {
  if (!className) return "";
  return styles[className] || "";
};

const renderNode = (node, keyPrefix, animated) => {
  const nodeType = node?.type || "block";

  if (nodeType === "group") {
    const layout = node?.layout || "column";
    const display = layout === "grid" ? "grid" : "flex";
    const flexDirection = layout === "row" ? "row" : "column";
    const gap = toCssLength(node?.gap ?? 12);

    const style = {
      display,
      gap,
      width: node?.width ? toCssLength(node.width) : undefined,
      alignItems: node?.alignItems,
      justifyContent: node?.justifyContent,
      flexDirection: display === "flex" ? flexDirection : undefined,
      gridTemplateColumns:
        display === "grid" && node?.columns
          ? `repeat(${node.columns}, minmax(0, 1fr))`
          : node?.gridTemplateColumns,
    };

    const nodeClassName = getNodeClassName(node?.className);

    return (
      <div
        key={keyPrefix}
        className={`${styles.group} ${nodeClassName}`}
        style={style}
        aria-hidden="true"
      >
        {(node?.items || []).map((child, childIndex) =>
          renderNode(child, `${keyPrefix}-${childIndex}`, animated),
        )}
      </div>
    );
  }

  const width = toCssLength(node?.width ?? defaultBlock.width);
  const height = toCssLength(node?.height ?? defaultBlock.height);
  const radius = toCssLength(node?.radius ?? defaultBlock.radius);

  const nodeClassName = getNodeClassName(node?.className);

  return (
    <div
      key={keyPrefix}
      className={`${styles.block} ${animated ? styles.animated : ""} ${nodeClassName}`}
      style={{
        width,
        height,
        borderRadius: radius,
      }}
      aria-hidden="true"
    />
  );
};

const SkeletonLayout = ({ items, animated, className }) => {
  const itemsToRender =
    Array.isArray(items) && items.length ? items : DEFAULT_ITEMS;

  return (
    <div className={`${styles.root} ${className || ""}`} aria-hidden="true">
      {itemsToRender.map((node, index) =>
        renderNode(node, `node-${index}`, animated),
      )}
    </div>
  );
};

SkeletonLayout.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  animated: PropTypes.bool,
  className: PropTypes.string,
};

SkeletonLayout.defaultProps = {
  items: null,
  animated: true,
  className: "",
};

export default SkeletonLayout;
